import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'RevTech RCM Suite',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Dental RCM software for U.S. DSOs including InsightFlow, DenialIQ, ClaimVision, PayerIQ, and ReconIQ — integrated with Xentrios PMS and NDS DataVault.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@type': 'Organization',
        name: 'RevTech Partners',
        url: 'https://revtechpartners.com',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is dental RCM software?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dental RCM (Revenue Cycle Management) software automates and manages the financial processes that move from a patient visit to collected payment. This includes electronic claim submission, insurance verification, denial tracking and appeal management, ERA/EOB payment posting, AR analytics, and payer contract performance monitoring. For multi-location DSOs, dental RCM software also provides enterprise-level reporting across locations, providers, and payers.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does RevTech integrate with my existing PMS?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'RevTech provides native connectors for Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, and Xentrios. The NDS DataVault layer handles ETL and data normalization, so RevTech analytics tools receive clean, consistent claim and patient data without requiring your team to manually export or reconcile records between systems.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does RevTech work for multi-location DSOs?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. RevTech is purpose-built for multi-location U.S. dental groups. InsightFlow provides enterprise AR roll-up across all locations, DenialIQ tracks denial rates by payer and location, and PayerIQ benchmarks your contracts across the group. All products support role-based access so location managers see their data while executives see the full enterprise view.',
          },
        },
        {
          '@type': 'Question',
          name: 'What U.S. dental payers does RevTech support?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'RevTech supports all major U.S. dental payers including Delta Dental, Cigna, MetLife, Aetna, Guardian, United Concordia, BlueCross BlueShield dental plans, Humana, Principal, and regional payers. PayerIQ maintains a payer-specific logic library with CARC and RARC code mappings for accurate denial categorization and appeal routing.',
          },
        },
      ],
    },
  ],
};

const products = [
  {
    icon: '📈',
    name: 'InsightFlow',
    desc: 'Enterprise AR analytics platform. Real-time aging by payer, provider, and location. Outstanding claim drill-down, net collection rate tracking, and customizable performance dashboards.',
  },
  {
    icon: '🚫',
    name: 'DenialIQ',
    desc: 'Denial management workspace for AR specialists. Automated denial categorization by CARC/RARC code, AI-assisted appeal letter generation, root cause analytics, and payer trend dashboards.',
  },
  {
    icon: '👁️',
    name: 'ClaimVision',
    desc: 'Claim intelligence layer that surfaces high-risk claims before they deny. Pre-submission scrubbing, coding accuracy checks, and eligibility verification integrated into the claim workflow.',
  },
  {
    icon: '🤝',
    name: 'PayerIQ',
    desc: 'Payer contract analytics and benchmarking. Track contracted rates vs. actual reimbursements, identify underpayments, and prepare data for contract renegotiation across all U.S. payers.',
  },
  {
    icon: '💳',
    name: 'ReconIQ',
    desc: 'Payment reconciliation platform. Match ERA/EOB payments to claims, identify short-pays and contractual adjustments, and close the loop between DenialIQ and the general ledger.',
  },
];

const integrations = ['Dentrix', 'Eaglesoft', 'Open Dental', 'CareStack', 'Planet DDS', 'Xentrios'];

export default function DentalRCMSoftwarePage() {
  const cmsPage = useCmsPage('xentrios', 'dental-rcm-software');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Dental Revenue Cycle Management Software for DSOs');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const productItems = c(cmsPage, 'features', 'items', products);
  const integrationItems = c(cmsPage, 'integrations', 'items', integrations);
  return (
    <div>
      <Helmet>
        <title>Dental Revenue Cycle Management Software for DSOs</title>
        <meta
          name="description"
          content="RevTech Partners delivers dental RCM software for U.S. DSOs: InsightFlow, DenialIQ, ClaimVision, PayerIQ, and ReconIQ. Integrated with Xentrios PMS and NDS DataVault. Purpose-built for multi-location dental groups."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Dental RCM Software</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* What is dental RCM software? */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Category</p>
          <h2>What is dental RCM software?</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Revenue cycle management software manages every financial process between a patient appointment
            and collected payment.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div>
              <p>
                For a dental practice, the revenue cycle begins when a patient is scheduled and insurance
                eligibility is verified. It continues through claim submission, adjudication, payment
                posting, and patient balance collection. Every step in that process — done manually — is a
                source of delay, error, and lost revenue.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Dental RCM software automates or assists with each of those steps. At the claim level, that
                means electronic submission, pre-submission scrubbing, and real-time status tracking. At the
                denial level, it means categorizing why a claim was denied, routing it to the right specialist,
                generating appeals, and tracking outcomes. At the analytics level, it means surfacing AR aging,
                net collection rates, denial rates by payer, and contract performance — across all locations.
              </p>
            </div>
            <div>
              <p>
                For multi-location DSOs, the stakes are higher. A single percentage point improvement in net
                collection rate across 50 locations represents significant annual revenue recovery. A one-day
                reduction in average days in AR at scale has measurable cash flow impact. And a denial rate
                that's two points above benchmark, undetected for a year, can represent millions in
                recoverable claims that were simply written off.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Generic RCM tools — or RCM tools built for hospitals and repurposed for dental — don't address
                the specific payer logic, CDT code structures, and multi-location reporting requirements of a
                U.S. dental group. RevTech was designed specifically for this environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RevTech product grid */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">RevTech Products</p>
          <h2>Five tools. One revenue cycle.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Each RevTech product targets a specific phase of the dental revenue cycle, and all five share
            the same data layer for seamless handoffs between stages.
          </p>
          <div className="xen-features">
            {productItems.map((p) => (
              <div key={p.name} className="xen-feature" style={{ textAlign: 'left' }}>
                <div className="xen-feature-icon" style={{ fontSize: '1.75rem' }}>{p.icon}</div>
                <h3 style={{ color: 'var(--xen-navy)' }}>{p.name}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why RevTech for U.S. DSOs */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Why RevTech</p>
          <h2>Why RevTech for U.S. DSOs</h2>
          <div className="xen-two-col" style={{ marginTop: '2rem', gap: '3rem' }}>
            <div>
              <h3 style={{ color: '#c62828', marginBottom: '1rem' }}>Problems with generic RCM tools</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.8, color: '#444' }}>
                <li>Built for medical billing — CDT code logic is bolted on, not native</li>
                <li>No understanding of multi-location dental payer contracts</li>
                <li>Require manual data exports from your PMS for every reporting cycle</li>
                <li>Denial management limited to simple category buckets — no CARC/RARC intelligence</li>
                <li>AR analytics are single-practice; enterprise roll-up requires spreadsheet work</li>
                <li>Payer-specific rules not maintained for U.S. dental plans</li>
                <li>Appeal templates not tailored to dental payer requirements</li>
                <li>Support teams unfamiliar with dental workflows or dental billing teams</li>
              </ul>
            </div>
            <div>
              <h3 style={{ color: '#388e3c', marginBottom: '1rem' }}>RevTech advantages</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.8, color: '#444' }}>
                <li>
                  <strong>Native PMS integration</strong> — direct connectors to Dentrix, Eaglesoft, Open
                  Dental, CareStack, Planet DDS, and Xentrios via the NDS data layer
                </li>
                <li>
                  <strong>NDS data layer</strong> — clean, normalized claim data without manual exports or
                  third-party ETL configuration
                </li>
                <li>
                  <strong>U.S. payer-specific logic</strong> — CARC/RARC code libraries maintained for all
                  major U.S. dental insurance carriers
                </li>
                <li>
                  <strong>Enterprise AR roll-up</strong> — InsightFlow shows AR aging across every location,
                  payer, and provider in one view
                </li>
                <li>
                  <strong>Denial-to-reconciliation handoff</strong> — DenialIQ and ReconIQ share the same
                  claim record so nothing falls through the cracks
                </li>
                <li>
                  <strong>Appeal intelligence</strong> — DenialIQ generates appeal letters specific to CDT
                  codes, payer, and denial reason
                </li>
                <li>
                  <strong>MaximizeRCM services layer</strong> — optional managed billing services backed by
                  the same technology your in-house team uses
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integration badges */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Integrations</p>
          <h2>Works with your existing PMS.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            RevTech connects to every major dental PMS in the United States through the NDS DataVault
            integration layer — no custom development required.
          </p>
          <div className="xen-integrations">
            {integrationItems.map((name) => (
              <div key={name} className="xen-integration-badge">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See RevTech in action for your DSO.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer about your current PMS, payer mix, and denial challenges.
            We'll show you exactly how the RevTech suite maps to your revenue cycle.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
